import {AxiosInstance, AxiosRequestConfig} from "axios";
import PartnerBrand, {IPartnerBrandPaymentsStatus, PartnerBrandSearch} from "../model/partnerBrand/PartnerBrand";
import {isDefined} from "../utils/Utils";
import PartnerBrandPoints from "../model/partnerBrand/PartnerBrandPoints";
import PartnerBrandRewardsData from "../model/partnerBrand/PartnerBrandRewardsData";
import {
	BoosterApiDto,
	CreateQuoteEmailTemplateApiDto,
	JobTypePbUpdateApiDto,
	PartnerBrandVehiclesImportApiDto,
	QuerySearchFilterApiDto
} from "./model/rest";
import QuoteEmailTemplate from "../model/partnerBrand/QuoteEmailTemplate";
import PartnerBrandBankData from "../model/partnerBrand/PartnerBrandBankData";
import QuoteEmailStyle from "../model/partnerBrand/QuoteEmailStyle";
import CustomFieldValue from "../model/job/CustomFieldValue";
import {FilterTypeEnum} from "../model/workspace/Filter";
import {api} from "./api";
import {WorkspaceName} from "../components/v5/table/workspace/WorkspaceContainer";
import DictionaryCache from "../helper/DictionaryCache";
import {ColumnTypeEnum} from "../model/workspace/ColumnDefinition";
import JobType from "../model/job/JobType";
import {BoostersMap, BoosterType} from "../model/booster/Booster";
import download = require("downloadjs");

/**
 * Partner Brand Api.
 */
export default class PartnerBrandApi {

	generateFleetForm(partnerBrandId: string): Promise<{fleetFormId: string}> {
		return this.axiosInstance.post(`/api/web/partner_brands/${encodeURIComponent(partnerBrandId)}/fleet-form`)
			.then(resp => resp.data);
	}

	/**
	 * Cache for searching partner brands.
	 * It prevents unnecessary request to the server.
	 */
	partnerBrandsCache: DictionaryCache<PartnerBrandSearch[]>;

	constructor(public readonly axiosInstance: AxiosInstance) {
		this.partnerBrandsCache = DictionaryCache.empty();
	}

	/**
	 * Fetch partner brands.
	 */
	fetchPartnerBrands(ids: string[], page = 1, pageSize: number) {
		const filters = [{
			columnType: ColumnTypeEnum.Uuid,
			operatorType: FilterTypeEnum.In,
			propertyName: "id",
			values: ids
		}]
		return api.instance.workspaceApi.fetchWorkspaceData(WorkspaceName.PartnerBrands, page, filters, "id,asc", pageSize)
	}

	/**
	 * Create partner brand.
	 * @returns {Promise<PartnerBrand>} Promise containing created partner brand.
	 */
	createPartnerBrand(data: PartnerBrand): Promise<PartnerBrand> {
		const url = `/api/web/partner_brands`;
		return this.axiosInstance.post(url, data.toJson()).then(resp => PartnerBrand.fromJson(resp.data));
	}

	/**
	 * Update partner brand.
	 * @returns {Promise<PartnerBrand>} Promise containing updated partner brand.
	 */
	updatePartnerBrand(partnerBrandId: string, data: PartnerBrand): Promise<PartnerBrand> {
		const url = `/api/web/partner_brands/${partnerBrandId}`;
		return this.axiosInstance.put(url, data.toJson()).then(resp => PartnerBrand.fromJson(resp.data));
	}

	/**
	 * Download partner brands xlsx
	 * @returns {Promise<void>}
	 */
	downloadPartnerBrands() {
		const url = `/api/web/partner_brands/export`;
		const query = {responseType: "blob"};
		return this.axiosInstance.get(url, query).then((resp) => {
			const disposition = resp.headers['content-disposition'];
			const filename = disposition.match(/filename=(.*)/)[1];
			download(resp.data, isDefined(filename) ? filename : "partnerBrands.xlsx");
		});
	}

	/**
	 * Upload partner brands xlsx
	 * @param {File} file
	 */
	uploadPartnerBrands(file: File) {
		const url = `/api/web/partner_brands/import`;
		const config: AxiosRequestConfig = {
			headers: {'content-type': 'multipart/form-data'},
			timeout: 600000
		};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}

	/**
	 * Upload partner brand vehicles file
	 * @param {string} partnerBrandId
	 * @param {File} file
	 */
	uploadPartnerBrandVehicles(partnerBrandId: string, file: File): Promise<PartnerBrandVehiclesImportApiDto> {
		const url = `/api/web/partner_brands/${partnerBrandId}/vehicles/import`;
		const config: AxiosRequestConfig = {
			headers: {'content-type': 'multipart/form-data'},
			timeout: 300000
		};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config).then(resp => resp.data);
	}

	/**
	 * Fetch partner brand job types configuration
	 * @param {string} partnerBrandId
	 * @returns {Promise<JobType[]>}
	 */
	fetchJobTypesConfig(partnerBrandId: string): Promise<JobType[]> {
		const url = `/api/web/partner_brands/${partnerBrandId}/job_types`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(JobType.fromJson));
	}

	/**
	 * Update partner brand job types configuration
	 * @param {string} partnerBrandId
	 * @param {string} jobTypes dto to update
	 */
	updateJobTypesConfig(partnerBrandId: string, jobTypes: JobTypePbUpdateApiDto[]) {
		const url = `/api/web/partner_brands/${partnerBrandId}/job_types`;
		return this.axiosInstance.put(url, jobTypes);
	}

	/**
	 * Fetch partner brand points
	 * @param {string} partnerBrandId
	 * @returns {Promise<PartnerBrandPoints[]>}
	 */
	fetchPoints(partnerBrandId: string): Promise<PartnerBrandPoints[]> {
		const url = `/api/web/partner_brands/${partnerBrandId}/points`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(PartnerBrandPoints.fromJson));
	}

	/**
	 * Add reward points
	 * @param {string} partnerBrandId
	 * @param {PartnerBrandPoints} points
	 * @returns {Promise<PartnerBrandPoints>}
	 */
	addPoint(partnerBrandId: string, points: PartnerBrandPoints): Promise<PartnerBrandPoints> {
		const url = `/api/web/partner_brands/${partnerBrandId}/points`;
		return this.axiosInstance.post(url, points.toCreateJson()).then(resp => PartnerBrandPoints.fromJson(resp.data));
	}

	/**
	 * Fetch partner brand rewards data.
	 * @param {string} partnerBrandId
	 * @returns {Promise<PartnerBrandRewardsData>}
	 */
	fetchRewardsData(partnerBrandId: string): Promise<PartnerBrandRewardsData> {
		const url = `/api/web/partner_brands/${partnerBrandId}/reward_campaign`;
		return this.axiosInstance.get(url).then(resp => PartnerBrandRewardsData.fromJson(resp.data));
	}

	/**
	 * Update partner brand rewards data.
	 * @param partnerBrandId
	 * @param rewardsData
	 */
	updateRewardsData(partnerBrandId: string, rewardsData: PartnerBrandRewardsData) {
		const url = `/api/web/partner_brands/${partnerBrandId}/reward_campaign`;
		return this.axiosInstance.put(url, rewardsData.toJson());
	}

	/**
	 * Accept reward points
	 * @param pointsId
	 */
	acceptPoint(pointsId: string) {
		const url = `/api/web/partner_brands/points/${pointsId}/submit`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Fetch all quote email templates
	 * @returns {Promise<QuoteEmailStyle[]>}
	 */
	fetchQuoteEmailStyles(): Promise<QuoteEmailStyle[]> {
		const url = '/api/web/quote_email_templates/styles';
		return this.axiosInstance.get(url).then(resp => resp.data.map(QuoteEmailStyle.fromJson));
	}

	/**
	 * Create new quote email template.
	 * @param quoteEmailTemplate
	 * @param partnerBrandId
	 * @returns {Promise<QuoteEmailTemplate>} Promise containing created quote email template.
	 */
	createQuoteEmailTemplate(quoteEmailTemplate: CreateQuoteEmailTemplateApiDto,
		partnerBrandId: string): Promise<QuoteEmailTemplate> {
		const url = `/api/web/partner_brands/${partnerBrandId}/quote_email_templates`;
		return this.axiosInstance.post(url, quoteEmailTemplate).then(resp => QuoteEmailTemplate.fromJson(resp.data));
	}

	/**
	 * Fetch all quote email templates
	 * @param partnerBrandId
	 * @returns {Promise<QuoteEmailTemplate[]>}
	 */
	fetchQuoteEmailTemplates(partnerBrandId: string): Promise<QuoteEmailTemplate[]> {
		const url = `/api/web/partner_brands/${partnerBrandId}/quote_email_templates`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(QuoteEmailTemplate.fromJson));
	}

	/**
	 * Remove quote email template
	 * @param {string} quoteEmailTemplateId
	 * @returns {AxiosPromise}
	 */
	removeQuoteEmailTemplate(quoteEmailTemplateId: string) {
		const url = `/api/web/quote_email_templates/${quoteEmailTemplateId}`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Fetch partner brand bank data.
	 * @param {string} partnerBrandId
	 * @returns {Promise<PartnerBrandBankData>}
	 */
	fetchBankData(partnerBrandId: string): Promise<PartnerBrandBankData> {
		const url = `/api/web/accounting/partner_brands/${partnerBrandId}/bank_data`;
		// TODO bring back after accounting service will be up
		// return this.axiosInstance.get(url).then(resp => PartnerBrandBankData.fromJson(resp.data));
		console.warn(url, "request has been mocked");
		return Promise.resolve(PartnerBrandBankData.createEmpty()); 
	}

	/**
	 * Fetch Partner brand custom fields values
	 * @param {string} partnerBrandId
	 * @returns {Promise<CustomFieldValue>}
	 */
	fetchPartnerBrandCustomFieldsValues(partnerBrandId: string): Promise<CustomFieldValue[]> {
		const url = `/api/web/partner_brands/${partnerBrandId}/custom_field_values`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(CustomFieldValue.fromJson));
	}

	/**
	 * Update partner brand custom field values
	 * @param {string} partnerBrandId
	 * @param {CustomFieldValue[]} customFieldsValues
	 * @returns {Promise<CustomFieldValue[]>}
	 */
	updatePartnerBrandCustomFieldsValues(partnerBrandId: string,
		customFieldsValues: CustomFieldValue[]): Promise<CustomFieldValue[]> {
		const url = `/api/web/partner_brands/${partnerBrandId}/custom_field_values`;
		return this.axiosInstance.put(url, customFieldsValues.map(item => item.toJson())).then(
			resp => resp.data.map(CustomFieldValue.fromJson));
	}

	/**
	 * Check partner brand payments status
	 * @param partnerBrandId
	 */
	checkPartnerBrandPaymentsStatus(partnerBrandId: string): Promise<IPartnerBrandPaymentsStatus> {
		const url = `/api/web/partner_brands/${partnerBrandId}/payment_status`;
		return this.axiosInstance.get(url).then(resp => resp.data);
	}

	/**
	 * Find partner brands by text.
	 * @param {string} text using to search.
	 * @param brandId
	 * @returns promise with results.
	 */
	findPartnerBrandsByText(text: string, brandId?: string): Promise<PartnerBrandSearch[]> {
		return new Promise((resolve, reject) => {
			const cachedData = this.partnerBrandsCache.get(brandId + text);
			if (cachedData.isDefined)
				resolve(cachedData.get);
			else {
				const filters: QuerySearchFilterApiDto[] = [{
					columnType: ColumnTypeEnum.String,
					operatorType: FilterTypeEnum.Equal,
					propertyName: "name",
					values: [text]
				}];
				if (brandId) {
					filters.push({
						columnType: ColumnTypeEnum.Brand,
						operatorType: FilterTypeEnum.In,
						propertyName: "brandId",
						values: [brandId]
					})
				}
				api.instance.workspaceApi.fetchWorkspaceData(WorkspaceName.PartnerBrands, 1,
					filters,
					['name,asc', 'id,asc'], 999).then(resp => {
					const data = resp.items.map(PartnerBrandSearch.fromJson);
					this.partnerBrandsCache.store(brandId + text, data);
					resolve(data);
				}).catch(reject);
			}
		});
	}

	/**
	 * Clear partner brand search cache.
	 */
	clearCache() {
		this.partnerBrandsCache = DictionaryCache.empty();
	}

	/**
	 * Fetch partner brand
	 * @param partnerBrandId
	 */
	fetchPartnerBrand(partnerBrandId: string): Promise<PartnerBrand> {
		const url = `/api/web/partner_brands/${partnerBrandId}`;
		return this.axiosInstance.get(url).then(resp => PartnerBrand.fromJson(resp.data))
	}

	/**
	 * Send download link by email
	 * @param partnerBrandId
	 */
	sendAllDocuments(partnerBrandId: string): Promise<any> {
		const url = `/api/web/partner_brands/${partnerBrandId}/send_documents`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Upload customers and leads xlsx
	 * @param partnerBrandId
	 * @param {File} file
	 */
	importCustomersAndLeads(partnerBrandId: string, file: File) {
		const url = `/api/web/partner_brands/${partnerBrandId}/customers_leads_import`;
		const config: AxiosRequestConfig = {headers: {'content-type': 'multipart/form-data'}};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}


	fetchPartnerBrandBoosters(partnerBrandId: string): Promise<BoostersMap> {
		const url = `/api/web/partner_brands/${partnerBrandId}/boosters`
		return this.axiosInstance.get(url)
			.then(resp => (resp.data as BoosterApiDto[]).reduce((map, item) => {
				map[item.booster] = item.enabled;
				return map
			}, {} as BoostersMap))
	}

	savePartnerBrandBoosters(partnerBrandId: string, boosters: BoostersMap) {
		const data: BoosterApiDto[] = Object.keys(boosters).map(
			(key: BoosterType) => {return {booster: key, enabled: boosters[key]}})
		const url = `/api/web/partner_brands/${partnerBrandId}/boosters`
		return this.axiosInstance.put(url, data);
	}
}
